import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { useCountSessionDetail } from '../hooks/use-count-session'
import { useCountRestaurantStore } from '../store/count-restaurant-store'
import {
  countItemGroup,
  expectedOnHand,
  formatCorrection,
  sumCorrections,
} from '../lib/count-item-summary'
import { COUNT_SESSION_STATUS_VARIANT } from '../lib/count-status-variant'
import type { CountItemGroup } from '../lib/count-item-summary'
import type { CountItemView } from '@/api/endpoints/inventory-count-sessions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { cn } from '@/lib/utils'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

// 'variance'/'notCounted' match CountItemGroup exactly (not the plural label text) so the filter
// predicate below can compare directly against `countItemGroup(item)` without a translation step.
type MainFilter = 'all' | 'variance' | 'notCounted'

const GROUP_ORDER: Record<CountItemGroup, number> = {
  variance: 0,
  notCounted: 1,
  match: 2,
}

export function CountSessionDetailPage() {
  const { t } = useTranslation('inventory-count')
  const { id = '' } = useParams({ strict: false })
  const restaurantId = useCountRestaurantStore((s) => s.restaurantId)
  const { data, isLoading } = useCountSessionDetail(restaurantId, id || null)
  const { can } = usePermissions()
  const canCount = can(ACTIONS.INVENTORY_COUNT_SESSION)
  const [filter, setFilter] = useState<MainFilter>('all')
  const unknownRef = useRef<HTMLDivElement>(null)

  // Hooks must run unconditionally on every render, so this is computed before the `!data` early
  // return below rather than after it (a real bug caught in browser verification: React threw
  // "Rendered more hooks than during the previous render" once `data` first resolved, because
  // `useMemo` used to sit after the early return).
  const items = data?.items ?? []
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const ga = GROUP_ORDER[countItemGroup(a)]
      const gb = GROUP_ORDER[countItemGroup(b)]
      if (ga !== gb) return ga - gb
      return a.name.localeCompare(b.name)
    })
  }, [items])

  if (!data) {
    return (
      <PermissionGate action={ACTIONS.INVENTORY_VIEW}>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold">{t('detail.title')}</h1>
            <Button variant="outline" asChild>
              <Link to="/inventur">{t('detail.back')}</Link>
            </Button>
          </div>
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-96" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('detail.loading')}
            </p>
          )}
        </div>
      </PermissionGate>
    )
  }

  const { session, unknownScans } = data

  const varianceCount = sortedItems.filter(
    (i) => countItemGroup(i) === 'variance',
  ).length
  const notCountedCount = sortedItems.filter(
    (i) => countItemGroup(i) === 'notCounted',
  ).length

  const visibleItems =
    filter === 'all'
      ? sortedItems
      : sortedItems.filter((i) => countItemGroup(i) === filter)

  const { up, down } = sumCorrections(items)

  const scrollToUnknown = () => {
    unknownRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }

  return (
    <PermissionGate action={ACTIONS.INVENTORY_VIEW}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-semibold">{t('detail.title')}</h1>
          <div className="flex items-center gap-2">
            {/* An open session is reachable from the history list, but this page is read-only —
                without this the counter is stranded here and has to find the Inventur entry point
                again to get the camera back (round-4 bug report). */}
            {session.status === 'OPEN' && canCount && (
              <Button asChild>
                <Link to="/inventory/count/capture">{t('detail.resume')}</Link>
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to="/inventur">{t('detail.back')}</Link>
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>
            {t('detail.headerLine', {
              date: new Date(session.startedAt).toLocaleString(),
              actor: session.startedBy,
            })}
          </span>
          {session.finishedAt && (
            <span>
              {t('detail.finishedLine', {
                date: new Date(session.finishedAt).toLocaleString(),
              })}
            </span>
          )}
          <Badge variant={COUNT_SESSION_STATUS_VARIANT[session.status]}>
            {t(`history.status.${session.status}`)}
          </Badge>
        </div>

        {items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('capture.listEmpty')}
          </p>
        ) : (
          <>
            <p className="text-sm tabular-nums text-muted-foreground">
              {t('detail.summaryItems', { count: items.length })}
              {' · '}
              {t('detail.summaryVariances', { count: varianceCount })}
              {' · '}
              {t('detail.summaryCorrection', {
                correction: formatCorrection(up, down),
              })}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <ToggleGroup
                type="single"
                variant="outline"
                value={filter}
                onValueChange={(v) => v && setFilter(v as MainFilter)}
              >
                <ToggleGroupItem value="all">
                  {t('detail.filters.all')} ({items.length})
                </ToggleGroupItem>
                {varianceCount > 0 && (
                  <ToggleGroupItem value="variance">
                    {t('detail.filters.variances')} ({varianceCount})
                  </ToggleGroupItem>
                )}
                {notCountedCount > 0 && (
                  <ToggleGroupItem value="notCounted">
                    {t('detail.filters.notCounted')} ({notCountedCount})
                  </ToggleGroupItem>
                )}
              </ToggleGroup>
              {unknownScans.length > 0 && (
                <Button variant="outline" size="sm" onClick={scrollToUnknown}>
                  {t('detail.filters.unknown')} ({unknownScans.length})
                </Button>
              )}
            </div>

            <Card>
              <CardContent className="overflow-x-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('detail.column.item')}</TableHead>
                      <TableHead className="hidden sm:table-cell">
                        {t('detail.column.sku')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('detail.column.counted')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('detail.column.expected')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('detail.column.diff')}
                      </TableHead>
                      <TableHead>{t('detail.column.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleItems.map((item) => (
                      <ItemRow key={item.sku} item={item} />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {unknownScans.length > 0 && (
          <Card ref={unknownRef}>
            <CardHeader>
              <CardTitle>{t('detail.unknownScans')}</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('detail.unknownColumn.code')}</TableHead>
                    <TableHead className="text-right">
                      {t('detail.unknownColumn.qty')}
                    </TableHead>
                    <TableHead>{t('detail.unknownColumn.time')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unknownScans.map((scan, idx) => (
                    <TableRow key={`${scan.code}-${idx}`}>
                      <TableCell className="font-mono">{scan.code}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {scan.qty}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(scan.capturedAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </PermissionGate>
  )
}

function ItemRow({ item }: { item: CountItemView }) {
  const { t } = useTranslation('inventory-count')

  return (
    <TableRow>
      <TableCell className="font-medium">{item.name}</TableCell>
      <TableCell className="hidden text-muted-foreground sm:table-cell">
        {item.sku}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {item.countedQty == null ? '—' : formatStockDisplay(item.countedQty)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatStockDisplay(expectedOnHand(item))}
      </TableCell>
      <TableCell
        className={cn(
          'text-right tabular-nums',
          item.delta != null &&
            item.delta > 0 &&
            'text-amber-600 dark:text-amber-400',
          item.delta != null && item.delta < 0 && 'text-destructive',
        )}
      >
        {item.delta != null
          ? item.delta > 0
            ? `+${formatStockDisplay(item.delta)}`
            : formatStockDisplay(item.delta)
          : '—'}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {rowStatusText(t, item)}
      </TableCell>
    </TableRow>
  )
}

function rowStatusText(
  t: (key: string, options?: Record<string, unknown>) => string,
  item: CountItemView,
): string {
  switch (item.resolution) {
    case 'CONFIRMED':
      return t('detail.rowStatus.confirmed', {
        reason: item.reason ? t(`review.reason.${item.reason}`) : '',
      })
    case 'CONFIRMED_ZERO':
      return t('detail.rowStatus.confirmedZero')
    case 'SKIPPED':
      return t('detail.rowStatus.ignored')
    case 'MATCHED':
      return t('detail.rowStatus.matched')
    default:
      return t('detail.rowStatus.open')
  }
}
